import { z } from 'zod';
import { messageKey } from './messages.js';
import { monitorKindSchema, monitorStatusSchema } from './monitors.js';
/**
 * Уведомления (ТЗ §11, фаза 9, задача 2): правила «условие → канал»,
 * каналы e-mail, Telegram и webhook, подавление шторма.
 */
// — Каналы ————————————————————————————————————————————————
export const NOTIFICATION_CHANNEL_KINDS = ['email', 'telegram', 'webhook'];
export const notificationChannelKindSchema = z.enum(NOTIFICATION_CHANNEL_KINDS);
/** Виды каналов — ключами словаря: подпись собирает браузер (§11, фаза 10). */
export const NOTIFICATION_CHANNEL_KIND_KEYS = {
    email: 'notifications.channelKind.email',
    telegram: 'notifications.channelKind.telegram',
    webhook: 'notifications.channelKind.webhook',
};
/**
 * Настройки канала e-mail.
 *
 * Только получатели. Сам SMTP-сервер задаётся окружением платформы
 * (`SMTP_URL`, `SMTP_FROM`), а не полем канала: пароль от почтового ящика —
 * секрет уровня установки, и хранить его копию в каждом канале означало бы
 * при смене пароля обходить их все.
 */
export const emailChannelConfigSchema = z.object({
    to: z
        .array(z.email(messageKey('validation.notifications.emailInvalid')))
        .min(1, messageKey('validation.notifications.recipientRequired'))
        .max(20),
});
/**
 * Настройки канала Telegram.
 *
 * `chatId` — куда писать: личный чат, группа (отрицательное число) или
 * канал (`@имя`). Токен бота секретен и в настройках не лежит: он приходит
 * отдельным полем и уходит в шифрованное хранилище (§10.2).
 */
export const telegramChannelConfigSchema = z.object({
    chatId: z.string().trim().min(1, messageKey('validation.notifications.chatIdRequired')).max(64),
    /** Кому сообщение приходит без звука — ночные уведомления по некритичному. */
    silent: z.boolean().default(false),
});
/**
 * Настройки webhook.
 *
 * Метод только POST: уведомление — это событие, а не запрос данных, и GET
 * с телом ни один приёмник не обещает понять. Заголовки произвольные —
 * приёмники требуют то `X-Api-Key`, то `Authorization`, и перечислить их
 * заранее нельзя.
 */
export const webhookChannelConfigSchema = z.object({
    url: z
        .url(messageKey('validation.notifications.urlInvalid'))
        .max(2000)
        .refine((value) => value.startsWith('http://') || value.startsWith('https://'), messageKey('validation.notifications.urlScheme')),
    /*
      Заголовки ограничены видимыми символами ASCII — это не наша строгость,
      а требование HTTP: значение заголовка кодируется latin-1, и русская
      буква в нём роняет отправку с сообщением «Cannot convert argument
      to a ByteString». Отказ обязан прозвучать здесь, при настройке,
      а не через неделю в журнале доставок — иначе выяснится это в момент
      первой же аварии.
    */
    headers: z
        .record(z
        .string()
        .max(100)
        .regex(/^[!-~]+$/, messageKey('validation.notifications.headerName')), z
        .string()
        .max(500)
        .regex(/^[ -~]*$/, messageKey('validation.notifications.headerValue')))
        .default({}),
    /**
     * Подписывать тело HMAC-SHA256 общим секретом.
     *
     * Приёмник обязан отличать наше уведомление от чужого запроса на тот же
     * адрес: webhook по определению открыт наружу. Подпись уезжает
     * в заголовке `X-NetDeck-Signature`.
     */
    sign: z.boolean().default(false),
});
export function channelConfigSchemaFor(kind) {
    switch (kind) {
        case 'email':
            return emailChannelConfigSchema;
        case 'telegram':
            return telegramChannelConfigSchema;
        case 'webhook':
            return webhookChannelConfigSchema;
    }
}
/** Нужен ли каналу секрет: токен бота, ключ подписи. */
export function channelNeedsSecret(kind) {
    return kind === 'telegram' || kind === 'webhook';
}
export const notificationChannelSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    kind: notificationChannelKindSchema,
    config: z.record(z.string(), z.unknown()),
    isEnabled: z.boolean(),
    /** Секрет задан. Само значение наружу не отдаётся никогда (§10.2). */
    hasSecret: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const createNotificationChannelSchema = z.object({
    name: z.string().trim().min(1).max(200),
    kind: notificationChannelKindSchema,
    config: z.record(z.string(), z.unknown()).default({}),
    /** Токен бота Telegram или ключ подписи webhook. */
    secret: z.string().min(1).max(500).optional(),
    isEnabled: z.boolean().default(true),
});
/**
 * Правка канала. Вид не меняется: у каждого вида своя форма настроек
 * и свой смысл секрета, и «поменять email на webhook» — это завести другой
 * канал, а не отредактировать этот.
 */
export const updateNotificationChannelSchema = z
    .object({
    name: z.string().trim().min(1).max(200),
    config: z.record(z.string(), z.unknown()),
    /** Пустая строка стирает секрет, отсутствие поля — оставляет прежний. */
    secret: z.string().max(500),
    isEnabled: z.boolean(),
})
    .partial();
// — Правила ————————————————————————————————————————————————
/**
 * Условие правила.
 *
 * Пустой список означает «любой», а не «ни одного»: правило без единого
 * ограничения — это «уведомлять обо всём», самый частый случай, и требовать
 * ради него перечислить все площадки и все виды проверок было бы издевательством.
 */
export const notificationRuleSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    channelId: z.uuid(),
    channelName: z.string(),
    channelKind: notificationChannelKindSchema,
    /** Площадки, за которыми следит правило; пусто — все. */
    siteIds: z.array(z.uuid()),
    /** Виды проверок; пусто — все. */
    monitorKinds: z.array(monitorKindSchema),
    /** Переходы, о которых сообщать. По умолчанию — только падения. */
    onStatuses: z.array(monitorStatusSchema),
    isEnabled: z.boolean(),
    createdAt: z.iso.datetime(),
});
export const createNotificationRuleSchema = z.object({
    name: z.string().trim().min(1).max(200),
    channelId: z.uuid(),
    siteIds: z.array(z.uuid()).max(200).default([]),
    monitorKinds: z.array(monitorKindSchema).max(4).default([]),
    onStatuses: z.array(monitorStatusSchema).min(1).max(3).default(['down']),
    isEnabled: z.boolean().default(true),
});
export const updateNotificationRuleSchema = createNotificationRuleSchema.partial();
// — Отправки ————————————————————————————————————————————
export const notificationDeliverySchema = z.object({
    id: z.uuid(),
    ruleId: z.uuid().nullable(),
    ruleName: z.string().nullable(),
    channelId: z.uuid().nullable(),
    channelName: z.string().nullable(),
    channelKind: notificationChannelKindSchema,
    status: z.enum(['sent', 'failed']),
    /** Сколько событий вошло в это уведомление. */
    eventCount: z.number().int().nonnegative(),
    /** Сколько событий подавлено правилом «раз в 30 минут по монитору». */
    suppressedCount: z.number().int().nonnegative(),
    subject: z.string(),
    error: z.string().nullable(),
    at: z.iso.datetime(),
});
export const notificationDeliveryQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
/** Ответ на проверку канала: дошло или нет и почему. */
export const testChannelResponseSchema = z.object({
    ok: z.boolean(),
    error: z.string().nullable(),
});
// — Подавление шторма ————————————————————————————————————
/**
 * Окно склейки: события, пришедшие за это время, уходят одним сообщением.
 *
 * Пять минут — из ТЗ. Ждать их целиком нельзя: критерий приёмки отводит
 * на доставку минуту. Поэтому окно работает не как задержка, а как область
 * склейки — уведомление уходит на ближайшем прогоне (см. `FLUSH_INTERVAL_MS`),
 * а всё, что за пять минут пришло по тому же монитору сверх первого, гасится
 * выдержкой ниже.
 */
export const AGGREGATION_WINDOW_MS = 5 * 60_000;
/** Не более одного уведомления по монитору в 30 минут (ТЗ, задача 2). */
export const MONITOR_COOLDOWN_MS = 30 * 60_000;
/**
 * Как часто разбирается накопленное.
 *
 * Тридцать секунд — компромисс между критерием «уведомление в течение
 * минуты» и склейкой: за полминуты успевают прийти все переходы одной
 * аварии, и в Telegram уходит одно сообщение про пять узлов, а не пять
 * сообщений про один каждое.
 */
export const FLUSH_INTERVAL_MS = 30_000;
/** Событие, из которого собирается уведомление. */
export const notificationEventSchema = z.object({
    monitorId: z.uuid(),
    monitorKind: monitorKindSchema,
    nodeId: z.uuid(),
    nodeName: z.string(),
    nodeAddress: z.string().nullable(),
    siteId: z.uuid(),
    siteName: z.string(),
    fromStatus: monitorStatusSchema,
    toStatus: monitorStatusSchema,
    at: z.iso.datetime(),
    error: z.string().nullable(),
});
/**
 * Подходит ли событие под условие правила.
 *
 * Живёт в контракте, а не в сервисе: по этому же правилу интерфейс
 * показывает, что именно правило поймает, и второе описание того же
 * условия на клиенте разошлось бы с серверным при первой правке.
 */
export function ruleMatches(rule, event) {
    if (!rule.isEnabled) {
        return false;
    }
    if (rule.siteIds.length > 0 && !rule.siteIds.includes(event.siteId)) {
        return false;
    }
    if (rule.monitorKinds.length > 0 && !rule.monitorKinds.includes(event.monitorKind)) {
        return false;
    }
    return rule.onStatuses.includes(event.toStatus);
}
/**
 * Заголовок уведомления.
 *
 * Одно событие называется по имени: «Склад: Шлюз не отвечает» — это то,
 * что видно в списке чатов и в теме письма, и по нему решают, бежать ли.
 * Несколько — считаются, потому что перечислить пять узлов в заголовке
 * нельзя, а «5 узлов не отвечают» так же понятно.
 */
export function notificationSubject(events) {
    const first = events[0];
    if (!first) {
        return 'NetDeck: событий нет';
    }
    if (events.length === 1) {
        return `NetDeck · ${first.siteName}: ${first.nodeName} — ${statusPhrase(first.toStatus)}`;
    }
    const down = events.filter((event) => event.toStatus === 'down').length;
    if (down === events.length) {
        return `NetDeck: ${down} ${pluralNodes(down)} не отвечают`;
    }
    return `NetDeck: ${String(events.length)} изменений состояния, из них падений — ${String(down)}`;
}
export function statusPhrase(status) {
    if (status === 'down') {
        return 'не отвечает';
    }
    if (status === 'up') {
        return 'снова в строю';
    }
    return 'состояние неизвестно';
}
function pluralNodes(count) {
    const mod100 = count % 100;
    if (mod100 >= 11 && mod100 <= 14) {
        return 'узлов';
    }
    const mod10 = count % 10;
    if (mod10 === 1) {
        return 'узел';
    }
    if (mod10 >= 2 && mod10 <= 4) {
        return 'узла';
    }
    return 'узлов';
}
/**
 * Текст уведомления — один на все три канала.
 *
 * Общий намеренно: сообщение в Telegram и письмо, рассказывающие об одной
 * аварии по-разному, — это два источника правды, и однажды они разойдутся.
 * Разметки здесь нет ровно поэтому: Markdown Telegram, HTML письма и JSON
 * webhook расходятся в экранировании, и общий текст обязан быть простым.
 */
export function renderNotificationText(events, suppressed = []) {
    const lines = [];
    for (const event of events) {
        const when = new Date(event.at).toLocaleString('ru-RU', { timeZone: 'UTC', hour12: false });
        const address = event.nodeAddress === null ? '' : ` (${event.nodeAddress})`;
        const reason = event.error === null ? '' : `, причина: ${event.error}`;
        lines.push(`${event.siteName} · ${event.nodeName}${address}: ${statusPhrase(event.toStatus)}` +
            ` — проверка ${event.monitorKind}${reason}. ${when} UTC`);
    }
    if (suppressed.length > 0) {
        lines.push('');
        lines.push('Подавлено как шторм (не более одного уведомления по монитору в 30 минут):');
        for (const item of suppressed) {
            lines.push(`${item.siteName} · ${item.nodeName}: ещё ${String(item.count)} ` +
                `${item.count === 1 ? 'смена' : 'смен'} состояния, сейчас ${statusPhrase(item.lastStatus)}`);
        }
    }
    return lines.join('\n');
}
/**
 * Тело webhook.
 *
 * Схема объявлена здесь, а не только на сервере, потому что это публичный
 * контракт: её читает чужой приёмник, и менять форму молча нельзя.
 */
export const webhookPayloadSchema = z.object({
    /** Версия формата тела. Приёмник вправе отказаться от незнакомой. */
    version: z.literal(1),
    subject: z.string(),
    text: z.string(),
    at: z.iso.datetime(),
    rule: z.object({ id: z.uuid(), name: z.string() }),
    events: z.array(notificationEventSchema),
    suppressed: z.array(z.object({
        monitorId: z.uuid(),
        nodeName: z.string(),
        siteName: z.string(),
        count: z.number().int().nonnegative(),
        lastStatus: monitorStatusSchema,
    })),
});
/** Заголовок с подписью тела webhook (HMAC-SHA256, hex). */
export const WEBHOOK_SIGNATURE_HEADER = 'x-netdeck-signature';
//# sourceMappingURL=notifications.js.map